import { useEffect, useRef } from "react";

/**
 * Horizontal chip rail for filters on mobile. Snaps + fades at edges.
 * Falls back to normal wrapping on wider screens (>= md).
 *
 * Props:
 *  - filters: [{ id, label, count?, tint? }]
 *  - value: currently selected id (empty string = "all")
 *  - onChange(id): called on select
 *  - testIdPrefix: prefix for chip data-testid, default "filter"
 */
export default function MobileFilterRail({ filters, value, onChange, testIdPrefix = "filter" }) {
  const activeRef = useRef(null);
  useEffect(() => {
    if (activeRef.current) {
      activeRef.current.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }
  }, [value]);

  return (
    <div
      className="relative -mx-4 md:mx-0 mb-4 md:mb-5"
      data-testid={`${testIdPrefix}-rail`}
    >
      {/* Edge fades (mobile only). Uses the page cream tint. */}
      <div className="pointer-events-none absolute inset-y-0 right-0 w-4 bg-gradient-to-l from-cream-50 to-transparent z-10 md:hidden" />
      <div className="pointer-events-none absolute inset-y-0 left-0 w-4 bg-gradient-to-r from-cream-50 to-transparent z-10 md:hidden" />
      <div
        className="flex items-center gap-2 overflow-x-auto snap-x snap-mandatory scrollbar-none px-4 md:px-0 py-1 md:flex-wrap md:overflow-visible"
        style={{ scrollbarWidth: "none" }}
      >
        {filters.map((f) => {
          const isActive = (value || "") === (f.id || "");
          return (
            <button
              key={f.id || "__all__"}
              type="button"
              ref={isActive ? activeRef : null}
              onClick={() => onChange(f.id)}
              data-testid={`${testIdPrefix}-${f.id || "all"}`}
              className={`snap-start shrink-0 inline-flex items-center gap-1.5 min-h-11 rounded-full px-4 text-sm font-bold transition-all active:scale-95 ${
                isActive
                  ? "bg-teal-700 text-white shadow-sm"
                  : "bg-white text-slate-700 border border-cream-200 hover:bg-slate-50"
              }`}
            >
              <span className="whitespace-nowrap">{f.label}</span>
              {f.count !== undefined && f.count !== null && (
                <span
                  className={`inline-flex items-center justify-center rounded-full min-w-[1.5rem] px-1.5 py-0.5 text-[11px] font-black arabic-numerals ${
                    isActive ? "bg-white/20 text-white" : "bg-slate-100 text-slate-700"
                  }`}
                >
                  {f.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
